import React, { useState } from 'react';

import { Image } from '@change/ds-core-ui';
import { PlayArrowIcon } from '@change/ds-icons';

import { useI18n } from '@change-corgi/core-react/i18n';

import { EmbeddedVideo } from '#src/app/shared/components/embeddedMedia';
import { useFocusFallbackRef } from '#src/app/shared/hooks/focus';

/**
 * Uses a facade pattern to prevent third party resources from loading until user interaction
 * https://web.dev/third-party-facades/
 */
type VideoProps = { html: string; aspectRatio: '1/1' | '16/9'; backgroundImage: string; alt: string };

const DS_ASPECT_RATIO = { '1/1': '1:1', '16/9': '16:9' } as const;

export const Video: React.FC<VideoProps> = ({ html, aspectRatio, backgroundImage, alt }) => {
	const { translate } = useI18n();
	const [loaded, setLoaded] = useState(false);
	const ref = useFocusFallbackRef();

	const handleLoadVideo = (e: React.SyntheticEvent<HTMLDivElement | HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setLoaded(true);
	};

	return (
		<div ref={ref}>
			{loaded ? (
				<EmbeddedVideo embedHtml={html} aspectRatio={aspectRatio} className="h-full" />
			) : (
				<div className="relative">
					<button
						type="button"
						aria-label={translate('corgi.home.a11y.load_video_button_label')}
						onClick={handleLoadVideo}
						className="absolute top-1/2 left-1/2 flex size-[70px] -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-0 bg-black/50 text-white"
					>
						<PlayArrowIcon size={40} />
					</button>
					<Image
						aspectRatio={DS_ASPECT_RATIO[aspectRatio]}
						src={backgroundImage}
						className="object-cover"
						onClick={handleLoadVideo}
						alt={alt}
					/>
				</div>
			)}
		</div>
	);
};
